import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChevronDown, ChevronUp, Lock, RotateCcw, Save, Info, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface NFLScoringSettingsProps {
  leagueId: string;
  isCommissioner: boolean;
  leagueStatus: string; // pre_draft, drafting, in_season, playoffs, completed
}

interface ModelTerm {
  applies_to: string;
  category: string;
  event: string;
  points: number;
  unit: string;
  source: string;
}

interface Override {
  applies_to: string;
  source: string;
  event: string;
  points: number;
}

/* ------------------------------------------------------------------ */
/*  Panel layout                                                       */
/* ------------------------------------------------------------------ */

// SAB-825: the rows in every panel come from nfl_scoring_model. This list decides only WHERE
// a term is DISPLAYED and in what order -- it holds no stats and no values.
//
// That distinction is the whole point. The old version of this screen hardcoded ~150 stats
// across 18 categories that shared no vocabulary with the model, so five position-split
// panels (Rushing QB/RB, Receiving TE/RB, Fumbles RB) existed that the model never scored,
// and Coach/DST were absent by construction. A panel here renders NOTHING unless the model
// has terms for it, so a phantom cannot come back.
//
// The 11 original panels keep their original keys, labels, descriptions and order. Five are
// appended for scoring roles the old layout had no place for. Seven are gone: the five
// phantoms plus `defense` and `defense_DL`, whose weights all live under applies_to='dst'
// (SAB-378a moved defense to the team unit) or the new IDP panel.
interface PanelConfig {
  key: string;
  label: string;
  description: string;
}

const PANELS: PanelConfig[] = [
  // --- original 11, verbatim, in their original order ---
  { key: 'passing', label: 'Passing', description: 'Quarterback passing stats' },
  { key: 'rushing', label: 'Rushing (General)', description: 'Non-position-specific rushing stats' },
  { key: 'receiving', label: 'Receiving (WR)', description: 'Wide receiver receiving stats' },
  { key: 'fumbles', label: 'Fumbles (General)', description: 'Ball security stats' },
  { key: 'field_goals', label: 'Field Goals', description: 'Kicker field goal stats' },
  { key: 'extra_points', label: 'Extra Points', description: 'Kicker PAT stats' },
  { key: 'punts', label: 'Punting', description: 'Punter stats' },
  { key: 'kick_returns', label: 'Kick Returns', description: 'Return specialist stats' },
  { key: 'punt_returns', label: 'Punt Returns', description: 'Punt return stats' },
  { key: 'kickoffs', label: 'Kickoffs', description: 'Kicker kickoff stats' },
  { key: 'penalties', label: 'Penalties', description: 'Penalty stats (all players)' },
  // --- added ---
  { key: 'coach', label: 'Coach', description: 'Coach stats (real team result)' },
  { key: 'dst', label: 'Team Defense / Special Teams', description: 'Team defense unit stats' },
  { key: 'idp', label: 'Defense (IDP)', description: 'Individual defensive player stats' },
  { key: 'general', label: 'General', description: 'Applies to every player' },
  { key: 'efficiency', label: 'Efficiency', description: 'Rate-based tier stats' },
];

const OLD_PANEL_KEYS = new Set(PANELS.slice(0, 11).map(p => p.key));

// Which panel a model term is displayed in. Total and deterministic: role and the three
// added categories are checked BEFORE the source prefix, because some of their terms share a
// prefix with an original panel -- `fumbles.opp_rec_tds` is an IDP term and must not fall
// into the Fumbles panel.
function panelKeyFor(t: ModelTerm): string | null {
  if (t.applies_to === 'coach') return 'coach';
  if (t.applies_to === 'dst') return 'dst';
  if (t.category === 'Defense (IDP)') return 'idp';
  if (t.category === 'General') return 'general';
  if (t.category === 'Efficiency') return 'efficiency';
  const prefix = t.source.split('.')[0];
  return OLD_PANEL_KEYS.has(prefix) ? prefix : null;
}

// A tiered_rate term's points are ignored by the scorer -- it reads tier_config.tiers[].points
// and never w.points -- so an override on one is a guaranteed no-op. These render through the
// existing read-only value branch rather than accepting input that would change nothing.
// Making tier ladders overridable is SAB-834.
const isTieredRate = (t: ModelTerm) => t.unit === 'tiered_rate';

// How a non-flat `unit` reads to a human, shown under the value.
//
// WHY THIS EXISTS: every value used to render as a bare number, so a term measured per 10
// yards looked identical to one measured per occurrence. "Individual penalty yards +10" read
// as 10 points per penalty when it is 10 per 10 YARDS -- a 45-yard pass interference is +45.
// Passing yards (-2 per 100) and Rushing yards (-0.5 per 10) both said "yards" and implied
// passing was worth 4x rushing when it is worth a fifth.
//
// `flat` is deliberately absent: silence means per-occurrence, which is what 75 of the 86
// terms are, and labelling all of them would bury the 11 that differ.
const UNIT_LABEL: Record<string, string> = {
  per_7_band: 'per 7-point band',
  per_point: 'per point',
  per_10_yards: 'per 10 yards',
  per_100_yards: 'per 100 yards',
  tiered_rate: 'tier ladder',
};

// Anything not `flat` gets a label, falling back to the RAW unit string when unmapped. A unit
// added to the model later must look odd rather than silently read as per-occurrence -- that
// silent-misread is the whole defect being fixed here.
function unitLabelFor(unit: string): string | null {
  if (unit === 'flat') return null;
  return UNIT_LABEL[unit] ?? unit;
}

// Mirrors overrideKey() in _shared/nfl-sabotage-scoring.ts and the write-time validator.
// Written as the ESCAPE '\0'; a literal NUL byte makes the file binary to git.
const SEP = '\0';
const termKey = (t: { applies_to: string; source: string; event: string }) =>
  t.applies_to + SEP + t.source + SEP + t.event;

// Map a save failure onto copy a commissioner can act on. Matching is on a stable fragment of
// each DB guard's message rather than on an error code, because several share errcode
// 22000/42501 and the code alone cannot tell them apart.
//
// ANYTHING UNMATCHED FALLS THROUGH TO A GENERIC LINE. Passing error.message straight to the
// toast would print raw Postgres at a commissioner and leak schema detail. The real text goes
// to the console instead so support can still see it.
function friendlySaveError(raw: string): string {
  const m = (raw || '').toLowerCase();
  if (m.includes('scored player-week rows')) {
    return 'This league has already scored games, so its scoring can no longer be changed.';
  }
  if (m.includes('frozen once the draft is complete')) {
    return 'Scoring is locked because your draft is complete.';
  }
  if (m.includes('does not score')) {
    return 'One of these rules is no longer part of the scoring model. Reload and try again.';
  }
  if (m.includes('is not a number')) {
    return 'One of the values is not a number. Check the highlighted rows and try again.';
  }
  if (m.includes('must be a json array of overrides')) {
    return 'This league’s saved scoring is in an old format that cannot be updated here. Contact support.';
  }
  if (m.includes('no active nfl_scoring_model')) {
    return 'Scoring rules are unavailable right now. Try again shortly.';
  }
  if (m.includes('row-level security') || m.includes('permission denied')) {
    return 'You do not have permission to change this league’s scoring.';
  }
  console.error('[nfl-scoring-settings] unmapped save error:', raw);
  return 'Could not save your scoring changes. Nothing was changed. Please try again.';
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function NFLScoringSettings({ leagueId, isCommissioner, leagueStatus }: NFLScoringSettingsProps) {
  const { toast } = useToast();
  const [terms, setTerms] = useState<ModelTerm[] | null>(null);
  const [overrides, setOverrides] = useState<Override[] | null>(null);
  // Current value per term key, and the values as last saved, so `hasChanges` is a comparison
  // of the same shape the old screen used.
  const [values, setValues] = useState<Record<string, number>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['passing', 'rushing', 'receiving']));

  const isLocked = leagueStatus !== 'pre_draft';
  const canEdit = isCommissioner && !isLocked;

  // Load the active model and this league's overrides together; both are needed before a
  // single row can be rendered with a correct value.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      supabase.from('nfl_scoring_model' as any).select('weights').eq('is_active', true).maybeSingle(),
      supabase.from('nfl_scoring_settings' as any).select('weights').eq('league_id', leagueId).maybeSingle(),
    ]).then(([modelRes, settingsRes]) => {
      if (cancelled) return;

      const mw = (modelRes.data as any)?.weights;
      if (modelRes.error || !Array.isArray(mw) || mw.length === 0) {
        // FAIL VISIBLE. Rendering zero panels would read as "nothing scores".
        setLoadError('Could not load the scoring rules.');
        setIsLoading(false);
        return;
      }
      const modelTerms = mw as ModelTerm[];

      const sw = (settingsRes.data as any)?.weights;
      const ov: Override[] = Array.isArray(sw) ? (sw as Override[]) : [];

      const byKey = new Map<string, number>();
      for (const o of ov) {
        if (o && typeof o.points === 'number') byKey.set(termKey(o), o.points);
      }
      const seeded: Record<string, number> = {};
      for (const t of modelTerms) {
        const k = termKey(t);
        seeded[k] = byKey.has(k) ? (byKey.get(k) as number) : t.points;
      }

      // Dev insurance: a term with no panel would silently vanish from the screen, which is
      // the defect class this ticket exists to remove. Nothing is rendered for it either way,
      // but it must not disappear quietly.
      const unplaced = modelTerms.filter(t => panelKeyFor(t) === null);
      if (unplaced.length > 0) {
        console.error('[nfl-scoring-settings] model terms with no panel:',
          unplaced.map(t => `${t.applies_to}/${t.source}/${t.event}`));
      }

      setTerms(modelTerms);
      setOverrides(ov);
      setValues(seeded);
      setOriginalValues(seeded);
      setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [leagueId]);

  const hasChanges = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(originalValues),
    [values, originalValues],
  );

  // Terms grouped into panels once, rather than filtered per panel on every render.
  const termsByPanel = useMemo(() => {
    const m = new Map<string, ModelTerm[]>();
    for (const t of terms ?? []) {
      const k = panelKeyFor(t);
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      (m.get(k) as ModelTerm[]).push(t);
    }
    for (const list of m.values()) list.sort((a, b) => a.event.localeCompare(b.event));
    return m;
  }, [terms]);

  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateWeight = (key: string, value: number) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!terms || !canEdit) return;
    setIsSaving(true);

    // Only terms that differ from the model are stored. An override equal to the model is
    // dead weight that reads as intent and would survive a model change as a stale pin, so
    // returning a value to its default REMOVES the override.
    const entries: Override[] = [];
    for (const t of terms) {
      if (isTieredRate(t)) continue; // never written; the scorer ignores its points
      const v = values[termKey(t)];
      if (typeof v === 'number' && Number.isFinite(v) && v !== t.points) {
        entries.push({ applies_to: t.applies_to, source: t.source, event: t.event, points: v });
      }
    }

    // Preserve any stored override this screen does not render -- a tiered_rate pin, or a key
    // from a future model this build does not know about. Dropping them would be silent loss.
    const renderable = new Set(terms.filter(t => !isTieredRate(t)).map(termKey));
    const preserved = (overrides ?? []).filter(o => o && !renderable.has(termKey(o)));
    const next = [...preserved, ...entries];

    const { error } = await supabase
      .from('nfl_scoring_settings' as any)
      .update({ weights: next, updated_at: new Date().toISOString() })
      .eq('league_id', leagueId);

    if (error) {
      toast({ title: 'Error', description: friendlySaveError(error.message), variant: 'destructive' });
    } else {
      setOverrides(next);
      setOriginalValues(values);
      toast({ title: 'Saved', description: 'Scoring weights updated' });
    }
    setIsSaving(false);
  };

  const handleReset = () => {
    if (!terms) return;
    const defaults: Record<string, number> = {};
    for (const t of terms) defaults[termKey(t)] = t.points;
    setValues(defaults);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Explainer */}
      <Card className="bg-gradient-to-br from-primary/5 to-card border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Sabotage Scoring</p>
              <p className="text-xs text-muted-foreground mt-1">
                <span className="text-damage font-bold">Positive values = TRASH</span>, bad plays you want (INTs, fumbles, missed kicks).
                {' '}
                <span className="text-emerald-400 font-bold">Negative values = ANTI</span>, good plays that hurt your score (TDs, yards).
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                Example: INT = +5.0 (trash), Pass TD = -2.75 (anti). Total = Trash + Anti.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Load failure — the rules are still in force, this screen just could not show them. */}
      {loadError && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="flex items-center gap-3 p-3">
            <Info className="h-4 w-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400">{loadError}</p>
          </CardContent>
        </Card>
      )}

      {/* Lock banner */}
      {isLocked && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="flex items-center gap-3 p-3">
            <Lock className="h-4 w-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400">
              Scoring rules are locked after the draft starts and cannot be changed mid-season.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      {canEdit && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleReset}
            className="gap-1.5 text-xs"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset to Defaults
          </Button>
          <div className="flex-1" />
          {hasChanges && (
            <span className="text-[10px] text-primary font-medium flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Unsaved changes
            </span>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="gap-1.5 text-xs bg-primary hover:bg-primary/90"
          >
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save Changes
          </Button>
        </div>
      )}

      {/* Category Sections */}
      {PANELS.map(cat => {
        const rows = termsByPanel.get(cat.key) ?? [];
        // A panel the active model has no terms for renders nothing. This is what stops a
        // phantom category from ever reappearing.
        if (rows.length === 0) return null;

        const isExpanded = expandedCategories.has(cat.key);
        const statCount = rows.length;

        return (
          <Card key={cat.key} className="bg-card border-border overflow-hidden">
            <button
              onClick={() => toggleCategory(cat.key)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{cat.label}</span>
                <span className="text-[10px] text-muted-foreground">({statCount} stats)</span>
              </div>
              {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>

            {isExpanded && (
              <CardContent className="pt-0 px-0 pb-2">
                {cat.description && (
                  <p className="text-[10px] text-muted-foreground px-4 pb-2">{cat.description}</p>
                )}
                <div className="divide-y divide-border/30">
                  {rows.map(t => {
                    const k = termKey(t);
                    const numVal = values[k] ?? t.points;
                    const isTrash = numVal > 0;
                    const isAnti = numVal < 0;
                    // tiered_rate falls to the read-only branch even for a commissioner.
                    const rowEditable = canEdit && !isTieredRate(t);
                    const unitLabel = unitLabelFor(t.unit);

                    return (
                      <div key={k} className="flex items-center gap-2 px-4 py-2">
                        {/* Stat name */}
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-foreground">{t.event}</span>
                        </div>

                        {/* Type badge */}
                        <Badge className={cn(
                          'text-[9px] px-1.5 py-0 border shrink-0',
                          isTrash ? 'bg-damage/10 text-damage border-damage/20'
                            : isAnti ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : 'bg-muted/10 text-muted-foreground border-border'
                        )}>
                          {isTrash ? 'TRASH' : isAnti ? 'ANTI' : '-'}
                        </Badge>

                        {/* Value. A non-flat unit is named under the number; without it a
                            per-10-yards term is indistinguishable from a per-occurrence one. */}
                        {rowEditable ? (
                          <div className="flex flex-col items-end gap-0.5 shrink-0">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => updateWeight(k, Math.round((numVal - 0.5) * 100) / 100)}
                                className="h-6 w-6 rounded bg-muted/30 hover:bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-foreground text-sm font-bold transition-colors"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                step="0.25"
                                value={numVal}
                                onChange={e => updateWeight(k, parseFloat(e.target.value) || 0)}
                                className="w-16 h-6 text-center text-xs font-bold bg-muted/20 border border-border rounded tabular-nums text-foreground"
                              />
                              <button
                                onClick={() => updateWeight(k, Math.round((numVal + 0.5) * 100) / 100)}
                                className="h-6 w-6 rounded bg-muted/30 hover:bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-foreground text-sm font-bold transition-colors"
                              >
                                +
                              </button>
                            </div>
                            {unitLabel && (
                              <span className="text-[10px] text-muted-foreground">{unitLabel}</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-end gap-0.5 w-16 shrink-0">
                            <span className={cn(
                              'text-xs font-bold tabular-nums',
                              isTrash ? 'text-damage' : isAnti ? 'text-emerald-400' : 'text-muted-foreground'
                            )}>
                              {numVal > 0 ? '+' : ''}{numVal}
                            </span>
                            {unitLabel && (
                              <span className="text-[10px] text-muted-foreground text-right leading-tight">{unitLabel}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
